/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { getAllUsersInSpace } from "@Actions/projectUsers";
import type { RootState } from "src/reducers";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import IconComponent from "@CubeComponents/icons";
import { setModulePermissions } from "@Components/ModuleUsersAccess/constants";

type ModuleRoleOption = ReturnType<typeof setModulePermissions>[number];

/**
 * A role id as this flow carries it: the numeric `folderRoles()` id (1..7), or `""` while no role
 * has been chosen. It has to stay numeric — it is seeded from `spaces.users[].modulePermissions`
 * (`role: z.number().nullable()`), it is set from `ModuleRoleOption["id"]`, and `SelectDropDown`
 * matches the selected option with `option[fields.value] === value`, so a stringified id would
 * silently stop resolving to a row and the picker would fall back to its placeholder.
 */
export type RoleId = number | "";

type PermissionFlowProps = {
  spaceId: string;
  getUsersCallBack: (usersRoles: { [userId: string]: RoleId }) => void;
  module: "ESTIMATES" | "CONTRACTS" | "SCHEDULES";
};

function PermissionFlow({ spaceId, getUsersCallBack, module }: Readonly<PermissionFlowProps>) {
  const { t } = useTranslation();
  const spaceUsers = useSelector((state: RootState) => state.spaceUsers);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const [bulkSelectedRole, setBulkSelectedRole] = useState<RoleId>("");
  const [usersRoles, setUsersRoles] = useState<{ [userId: string]: RoleId }>({});

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getAllUsersInSpace({ spaceId }));
  }, [spaceId]);

  useEffect(() => {
    setRoleForAllUsers();
  }, [spaceUsers]);

  useEffect(() => {
    if (Object.keys(usersRoles).length) {
      getUsersCallBack(usersRoles);
    }
  }, [usersRoles]);

  const handleBulkRuleChange = (option: ModuleRoleOption) => {
    setBulkSelectedRole(option.id);
  };

  const setRoleForAllUsers = (bulkSelectedRole?: RoleId) => {
    const allSelectedUserRole: { [userId: string]: RoleId } = { ...usersRoles };
    spaceUsers.map((data) => {
      allSelectedUserRole[data.id] = bulkSelectedRole || data.modulePermissions?.[module]?.role || "";
    });
    setUsersRoles(allSelectedUserRole);
  };

  const setUserRole = (option: ModuleRoleOption, userId: string) => {
    const allUserRoles: { [userId: string]: RoleId } = { ...usersRoles };
    allUserRoles[userId] = option.id;
    setUsersRoles(allUserRoles);
  };

  const itemTemplate = (data: ModuleRoleOption) => {
    return (
      <div className="flex gap-3 p-1 items-center">
        <div>
          <IconComponent name={data.name} width="1.5rem" height="1.5rem" />
        </div>
        <div className="flex flex-col">
          <div className="font-semibold">{data.frontendName}</div>
          <div className="text-xs">{data.description}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-2 h-125 flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <span className="font-semibold">{t("Select bulk permission to users")}</span>
        <div className="flex gap-3 items-center border border-[#C6D8EC] p-4">
          <span className="whitespace-nowrap font-semibold">{t("Permission")}</span>
          <SelectDropDown
            renderOption={itemTemplate}
            onChange={handleBulkRuleChange}
            value={bulkSelectedRole}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            fields={{ text: "frontendName", value: "id" }}
            options={setModulePermissions(module)}
            placeholder={t("Select Role")}
            buttonWidth="100%"
            popupAlign="right"
            popperWidth={400}
          />
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            disabled={!bulkSelectedRole}
            onClick={() => setRoleForAllUsers(bulkSelectedRole)}
          >
            {t("Bulk update")}
          </ButtonComponent>
        </div>
      </div>
      <div className="flex flex-col gap-5 overflow-auto">
        <div className="flex items-center gap-2">
          <span className="font-semibold whitespace-nowrap">{t(".. or update user permission individually")}</span>
        </div>
        <div className="flex flex-col gap-4 overflow-auto">
          <div className="flex">
            <span className="flex-1 font-semibold">{t("Users")}</span>
            <span className="flex-1 font-semibold">{t("Permissions")}</span>
          </div>
          {spaceUsers.map(
            (user) =>
              user.id !== userDetails._id && (
                <div key={user.id} className="flex items-center">
                  <span className="flex-1">
                    <UserNameTagComponent id={user.id} />
                  </span>
                  <span className="flex-1">
                    <SelectDropDown
                      renderOption={itemTemplate}
                      onChange={(value) => {
                        if (value && !Array.isArray(value)) setUserRole(value as ModuleRoleOption, user.id);
                      }}
                      value={usersRoles[user.id] ?? null}
                      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                      fields={{ text: "frontendName", value: "id" }}
                      options={setModulePermissions(module)}
                      placeholder={t("Select Project Role")}
                      buttonWidth="100%"
                      popupAlign="right"
                      popperWidth={400}
                    />
                  </span>
                </div>
              ),
          )}
        </div>
      </div>
    </div>
  );
}

export default PermissionFlow;
